/* eslint-disable */
import React, { useEffect, useState, useRef } from "react";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import config from "../../config";
import { useParams } from "react-router-dom";
import useUserContext from "../../hooks/useUserContext";

export default function NHBTerms({ document }) {
    const params = useParams();
    const {
        userState: { accessToken },
    } = useUserContext();
    const [termsData, setTermsData] = useState(document || null);
    const contentRef = useRef(null);

    useEffect(() => {
        // If a document was passed as prop, we don’t need to fetch again
        if (document) return;

        const fetchTermsData = async () => {
            try {
                const response = await fetch(
                    `${config.API_BASE_URL}/api/nhb/documents/${params.documentId}`,
                    {
                        headers: { Authorization: `Bearer ${accessToken}` },
                    }
                );

                if (!response.ok) throw new Error("Failed to fetch NHB document");
                const data = await response.json();

                const merged = data?.data?.nhbTerms
                    ? { ...data.data, ...data.data.nhbTerms }
                    : data.data;

                setTermsData(merged);
            } catch (error) {
                console.error("Error fetching NHB document:", error);
            }
        };

        if (params.documentId && accessToken) fetchTermsData();
    }, [params.documentId, accessToken, document]);

    const downloadPDF = async () => {
    const input = contentRef.current;
    if (!input) return;

    const images = input.querySelectorAll('img');
    const imageLoadPromises = Array.from(images).map((img) => {
        if (!img.complete) {
            return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
            });
        }
        return Promise.resolve();
    });
    await Promise.all(imageLoadPromises);

    const canvas = await html2canvas(input, {
        useCORS: true,
        allowTaint: false,
        scale: 1.5, // smaller than 2
        backgroundColor: '#ffffff',
        scrollY: -window.scrollY,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.8); // JPEG with quality 0.8
    const imgWidth = 210;
    const pageHeight = 297;

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgProps = pdf.getImageProperties(imgData);
    const pdfHeight = (imgProps.height * imgWidth) / imgProps.width;

    let heightLeft = pdfHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, pdfHeight, undefined, 'FAST');
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
        position = position - pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, pdfHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
    }

    pdf.save('NHBTermsAndConditions.pdf');
};


    if (!termsData)
        return (
            <div className="text-center text-gray-600 py-10 text-lg">
                Loading document...
            </div>
        );

    const terms = termsData.nhbTerms ? { ...termsData, ...termsData.nhbTerms } : termsData;

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col items-center py-10 px-5" >
            <div className="mx-auto max-w-7xl px-6 lg:px-8" ref={contentRef}>
                <div className="mx-auto max-w-2xl lg:text-center">
                    <div className="items-center">
                        <div>
                            <img
                                className="mx-auto mt-4 mb-4 h-auto w-36"
                                src="https://cohatch-media-assets.nyc3.cdn.digitaloceanspaces.com/static/images/NorthHigh_Full_Black.png"
                                alt="North High Logo"
                            />
                        </div>
                    </div>
                    <p className="mt-2 text-pretty text-2xl font-semibold tracking-tight text-gray-900 sm:text2xl lg:text-balance">
                        Private Events Terms & Conditions
                    </p>
                    <p className="mt-6 text-sm/8 text-black">
                        We are so excited that you chose North High to host your event! The
                        invoice for the non-refundable Reservation Fee has just been sent to
                        you by email from Toast. By paying the invoice, your reservation
                        will now be secured and you agree to the following terms and details
                        regarding your event:
                    </p>
                </div>

                {/* Cards Grid */}
                <div className="isolate mx-auto mt-6 grid max-w-md grid-cols-1 gap-4 text-center lg:mx-0 lg:max-w-none lg:grid-cols-2">
                    {/* Headings above the cards */}
                    <div>
                        <h3 className="text-xl font-semibold text-black">Timeline</h3>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-black">Reminders</h3>
                    </div>

                    {/* Timeline Card */}
                    <div className="p-8 xl:p-4">
                        <ul className="space-y-3 text-sm text-black text-left">
                            <li>
                                <strong>• Time of Booking:</strong> Reservation fee collected.
                                This fee holds the date and space, and is non refundable in the
                                event of cancellation.
                            </li>
                            <li>
                                <strong>• At least 14 days before event:</strong> Host has walked
                                through space with Event Manager.
                            </li>
                            <li>
                                <strong>• 7 days prior to event:</strong> Catering order must be
                                submitted. Fees may apply to late orders.
                            </li>
                            <li>
                                <strong>• Tuesday before event:</strong> Host to confirm final
                                guest count.
                            </li>
                            <li>
                                <strong>• 30 minutes prior to event:</strong> Host (and up to 5
                                guests) will have access to the space for decorating. An
                                additional 30 minutes can be added for $50.
                            </li>
                        </ul>
                    </div>

                    {/* Reminders Card */}
                    <div className="p-8 xl:p-4">
                        <ul className="space-y-3 text-sm text-black text-left">
                            <li>
                                <strong>•</strong> Reservation fee does not count toward Food &
                                Beverage Minimum.
                            </li>
                            <li>
                                <strong>•</strong> Reservations are for 3 hours. Each additional
                                hour is a $150 flat rate on top of minimum.
                            </li>
                            <li>
                                <strong>•</strong> 20% gratuity added on parties of 15+
                            </li>
                            <li>
                                <strong>•</strong> Spaces with private bars include an Event
                                Bartender. Larger events may require an additional bartender,
                                which is an additional $100 upcharge per bartender.
                            </li>
                            <li>
                                <strong>• Clean Up:</strong> Host is responsible for cleaning up
                                any outside items you bring in. A fee of $100 will be charged for
                                clean up of decorations left behind.
                            </li>
                            <li>
                                <strong>• Damage to Venue:</strong> Host is responsible for any
                                damage to the property caused by guests. This includes, but is
                                not limited to, damage to furniture, fixtures, and equipment
                                beyond normal wear and tear. Host agrees to pay the cost of
                                repairing damages.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Headings above the cards */}
                <div>
                    <h3 className="mt-4 text-xl font-semibold text-center text-black">
                        Food & Beverage Minimums
                    </h3>
                </div>
                <div className="isolate mx-auto mt-6 grid max-w-md grid-cols-1 gap-4 text-center lg:mx-0 lg:max-w-none lg:grid-cols-2">
                    {/* Timeline Card */}
                    <div className="p-8 xl:p-4">
                        <ul className="space-y-3 text-sm text-black text-left">
                            <li>
                                Food and Beverage Minimums are charged on either a per-person
                                basis (Sunday-Thursday) or a Weekend Rate (Friday-Saturday). For
                                per-person events, Host to confirm expected guest count by end of
                                day Tuesday before to determine Food and Beverage Minimum.
                            </li>
                            <li>
                                At the end of the event, spend on catering, food, and drinks are
                                totaled up. If under the minimum, Event Minimum Difference Fee
                                will be charged to the Host.
                            </li>
                        </ul>
                    </div>
                    <div className="p-8 xl:p-4">
                        <ul className="space-y-3 text-sm text-black text-left">
                            <li>
                                <strong>•Cash Bar</strong> - Guests open their own tabs. All tabs
                                opened for the event are applied toward minimum.
                            </li>
                            <li>
                                <strong>•Host Bar</strong> - All Guests order on Host’s Tab.
                            </li>
                            <li>
                                <strong>•Capped Host Bar</strong> - Host decides their spend
                                limit. Once the cap is reached, the bar will convert to cash bar
                                for Guests.
                            </li>
                            <li>
                                <strong>•Limited Host Bar</strong> - Guest options are limited to
                                beer, wine, seltzers, and ciders. Guests may open their own tabs
                                for cocktails or spirit.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Container Box */}
                <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 space-y-12 border border-gray-200">
                    {/* CONTACT INFORMATION */}
                    <section className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Contact Information</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                            {/* Full Name */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Full Name</h3>
                                <p className="text-base text-gray-900">
                                    {document?.firstName || document?.lastName
                                        ? `${document.firstName || ""} ${document.lastName || ""}`.trim()
                                        : "—"}
                                </p>
                            </div>

                            {/* Email */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Email Address</h3>
                                <p className="text-base text-gray-900">
                                    {document?.emailAddress || "—"}
                                </p>
                            </div>

                            {/* Phone */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Phone Number</h3>
                                <p className="text-base text-gray-900">
                                    {document?.phoneNumber || "—"}
                                </p>
                            </div>

                            {/* Event Description */}
                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Event Description</h3>
                                <p className="text-base text-gray-900 whitespace-pre-line leading-relaxed">
                                    {document?.nhbTerms?.eventDescription || "—"}
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* EVENT DETAILS */}
                    <section className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Event Details</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-x-8 gap-y-6">
                            <div className="sm:col-span-3">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Event Space</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.eventSpace || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-3">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Location</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbRestaurant || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Date</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.reservationDate
                                        ? new Date(document.nhbTerms.reservationDate).toLocaleDateString("en-US", { timeZone: "UTC" })
                                        : "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Time</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.eventTime || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Guests</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.numberOfGuests || "—"}
                                </p>
                            </div>
                        </div>
                        <p className="mt-6 text-sm italic text-right text-gray-600">
                            * Reservations secured after the Reservation Fee is paid (non-refundable)
                        </p>
                    </section>

                    {/* ADDITIONAL DETAILS */}
                    <section className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Additional Details</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-x-8 gap-y-6">
                            <div className="sm:col-span-3">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Dining Type</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.diningType || "—"}
                                </p>
                            </div>
                            <div className="sm:col-span-3">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Order Deadline</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.orderDeadline || "—"}
                                </p>
                            </div>
                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Bar Type</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.barType || "—"}
                                </p>
                            </div>
                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Host Capacity</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.hostCap || "—"}
                                </p>
                            </div>
                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Check Type</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.checkType || "—"}
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* FEES */}
                    <section className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Fees</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-x-8 gap-y-6">
                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Reservation Fee</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.reservationFee
                                        ? `$${document.nhbTerms.reservationFee}`
                                        : "—"}
                                </p>
                            </div>
                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Food & Beverage Minimum</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.fbMinimum
                                        ? `$${document.nhbTerms.fbMinimum}`
                                        : "—"}
                                </p>
                            </div>
                            <div className="sm:col-span-2">
                                <h3 className="text-sm font-medium text-gray-700 mb-1">Additional Fees</h3>
                                <p className="text-base text-gray-900">
                                    {document?.nhbTerms?.additionalFees
                                        ? `$${document.nhbTerms.additionalFees}`
                                        : "—"}
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Signature Section */}
                    <section className="flex flex-col sm:flex-row gap-10 items-start">
                        <div className="flex-1 sm:w-2/3">
                            {termsData?.signature ? (
                                <>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Signature Preview</h3>
                                    <img
                                        src={termsData.signature}
                                        alt="Signature Preview"
                                        className="rounded-md border border-gray-300 p-4 shadow-md max-w-full h-auto mx-auto sm:mx-0"
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="sm:mt-0 sm:text-left self-start p-4 bg-gray-50 rounded-lg shadow-md">
                                        <h3 className="text-xl font-semibold text-danger-500">
                                            SIGNATURE - Not yet submitted by user.
                                        </h3>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </div>
            </div>
            <div className="text-center">
                <button onClick={downloadPDF} className=" bg-primary-400 text-white py-2 px-4 rounded">
                    Download PDF
                </button>
            </div>
        </div>
    );
}
